import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {useTheme} from "@mui/material/styles";
import NewTranscriptionLangDropdown from "../NewStudio/NewTranscriptionLangDropdown";
import {formatToReadableLargeTitle, LibrettoProjectTypes} from "../../utils/utils";
import {validateFileForUpload} from "../../utils/fileValidation";
import {ExtractMediaFileMetadata} from "../../storage/Media";
import {useAssetStore} from "../../storage/AssetStore";

const viewStates = {
    SELECT_FILE: "select-file",
    REVIEW_AND_UPLOAD: "review-and-upload",
};

const waveformColumns = [18, 34, 26, 40, 22, 30, 38, 24, 32, 20, 36, 18, 26, 34, 28];
const imageFilePattern = /\.(bmp|jpe?g|png|gif|webp)$/i;

const getUploadFiletype = (file) => (file?.type?.startsWith("video/") ? "video" : "audio");

const formatFileName = (name = "") => {
    const trimmed = formatToReadableLargeTitle(name || "");
    if (trimmed.length <= 74) return trimmed;
    return `${trimmed.slice(0, 74)}...`;
};

const formatMediaDuration = (durationSec) => {
    if (!Number.isFinite(durationSec) || durationSec <= 0) return "";

    const totalSeconds = Math.max(0, Math.round(durationSec));
    if (totalSeconds < 60) {
        return `${totalSeconds}s`;
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const AudioWaveformPreview = () => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                background: "linear-gradient(180deg, var(--app-bg-surface) 0%, var(--app-bg-subtle) 100%)",
                border: "1px solid var(--app-border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                px: "26px",
            }}
        >
            {waveformColumns.map((height, idx) => (
                <Box
                    key={`${height}-${idx}`}
                    sx={{
                        width: "6px",
                        height: `${height}px`,
                        borderRadius: "999px",
                        backgroundColor: idx % 3 === 0 ? "#2B6BFD" : "var(--app-icon-primary)",
                        opacity: idx % 3 === 0 ? 0.8 : 0.65,
                    }}
                />
            ))}
        </Box>
    );
};

const MediaPreviewCard = ({file, showMeta = false}) => {
    const [previewUrl, setPreviewUrl] = useState("");
    const [aspectRatio, setAspectRatio] = useState("landscape");
    const [durationLabel, setDurationLabel] = useState("");

    const isVideo = Boolean(file?.type?.startsWith("video/"));
    const isAudio = Boolean(file?.type?.startsWith("audio/"));

    useEffect(() => {
        if (!isVideo || !file) {
            setPreviewUrl("");
            setAspectRatio("landscape");
            if (!isAudio) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const videoUrl = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.src = videoUrl;
        video.preload = "metadata";
        video.muted = true;

        const cleanupVideoUrl = () => {
            URL.revokeObjectURL(videoUrl);
        };

        video.onloadedmetadata = () => {
            if (cancelled) return;
            const ratio = (video.videoWidth || 1) / (video.videoHeight || 1);
            if (ratio > 1.1) setAspectRatio("landscape");
            else if (ratio < 0.9) setAspectRatio("portrait");
            else setAspectRatio("square");
            setDurationLabel(formatMediaDuration(video.duration));

            const seekTarget = Number.isFinite(video.duration) && video.duration > 1 ? 1 : 0;
            const safeTarget = Math.max(0, Math.min(seekTarget, (video.duration || 0) - 0.05));
            video.currentTime = Number.isFinite(safeTarget) ? safeTarget : 0;
        };

        video.onseeked = () => {
            if (cancelled) return;
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                cleanupVideoUrl();
                return;
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            setPreviewUrl(canvas.toDataURL("image/jpeg", 0.85));
            cleanupVideoUrl();
        };

        video.onerror = () => {
            cleanupVideoUrl();
        };

        return () => {
            cancelled = true;
            cleanupVideoUrl();
        };
    }, [file, isVideo]);

    useEffect(() => {
        if (!isAudio || !file) {
            if (!isVideo) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const audioUrl = URL.createObjectURL(file);
        const audio = document.createElement("audio");
        audio.src = audioUrl;
        audio.preload = "metadata";

        audio.onloadedmetadata = () => {
            if (cancelled) return;
            setDurationLabel(formatMediaDuration(audio.duration));
            URL.revokeObjectURL(audioUrl);
        };

        audio.onerror = () => {
            if (cancelled) return;
            setDurationLabel("");
            URL.revokeObjectURL(audioUrl);
        };

        return () => {
            cancelled = true;
            URL.revokeObjectURL(audioUrl);
        };
    }, [file, isAudio, isVideo]);

    const mediaStyle = (() => {
        if (aspectRatio === "portrait" || aspectRatio === "square") {
            return {
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
            };
        }
        return {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        };
    })();

    const metaText = [formatFileName(file?.name), durationLabel].filter(Boolean).join(" \u2022 ");

    return (
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: "8px"}}>
            <Box
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-subtle)",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {isVideo && previewUrl ? (
                    <img src={previewUrl} alt="Uploaded video preview" style={mediaStyle}/>
                ) : null}
                {isAudio ? <AudioWaveformPreview/> : null}
            </Box>
            {showMeta && metaText ? (
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                        textAlign: "left",
                    }}
                >
                    {metaText}
                </Box>
            ) : null}
        </Box>
    );
};

const Header = ({onClose}) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                gap: "16px",
            }}
        >
            <Box sx={{display: "flex", flexDirection: "column", gap: "4px", pr: "10px"}}>
                <Box
                    sx={{
                        color: "var(--app-text-primary)",
                        fontFamily: "Inter",
                        fontSize: "20px",
                        fontWeight: 600,
                        lineHeight: "28px",
                        letterSpacing: "-0.01em",
                    }}
                >
                    Upload and start editing
                </Box>
                <Box
                    sx={{
                        color: "var(--app-text-muted)",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                    }}
                >
                    Transcribes file and loads a composition so you can edit by text or on the timeline.
                </Box>
            </Box>
            <Box
                sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "10px",
                    border: "1px solid var(--app-border-subtle)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "var(--app-text-muted)",
                    "&:hover": {
                        backgroundColor: "var(--app-bg-subtle)",
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={onClose}
            >
                <CloseIcon sx={{fontSize: "20px"}}/>
            </Box>
        </Box>
    );
};

const SelectFileState = ({onFileChoose, validationMessage, isDarkMode = false}) => {
    const inputRef = useRef(null);
    const dragDepthRef = useRef(0);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleChange = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;
        await onFileChoose(file);
        if (e?.target) {
            e.target.value = "";
        }
    };

    const resetDragState = () => {
        dragDepthRef.current = 0;
        setIsDragActive(false);
    };

    const getDroppedFile = (e) => {
        const items = Array.from(e?.dataTransfer?.items || []);
        for (const item of items) {
            if (item?.kind !== "file") continue;
            const file = item.getAsFile?.();
            if (file) return file;
        }
        const files = e?.dataTransfer?.files;
        return files?.[0] || null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragActive(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e?.dataTransfer) {
            e.dataTransfer.dropEffect = "copy";
        }
        if (!isDragActive) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = getDroppedFile(e);
        resetDragState();
        if (!file) return;
        await onFileChoose(file);
    };

    return (
        <Box sx={{width: "100%"}}>
            <Box
                component="label"
                htmlFor="upload-and-edit-upload"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    border: `1px dashed ${isDragActive ? "#2B6BFD" : "var(--app-border-muted)"}`,
                    backgroundColor: isDragActive
                        ? (isDarkMode ? "rgba(43, 107, 253, 0.18)" : "#F3F8FF")
                        : "var(--app-bg-canvas)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                        backgroundColor: isDarkMode ? "rgba(43, 107, 253, 0.12)" : "var(--app-bg-subtle)",
                        borderColor: "var(--app-border-muted)",
                    },
                }}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "20px",
                    }}
                >
                    Upload video or audio
                </Box>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                    }}
                >
                    {isDragActive ? "Drop file to upload." : "Click to browse or drag and drop your file."}
                </Box>
            </Box>
            <input
                ref={inputRef}
                id="upload-and-edit-upload"
                type="file"
                accept="video/*, audio/*"
                hidden
                onChange={handleChange}
            />
            {validationMessage ? (
                <Box
                    sx={{
                        mt: "12px",
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {validationMessage}
                </Box>
            ) : null}
        </Box>
    );
};

const ReviewState = ({
    selectedFile,
    selectedLanguage,
    setSelectedLanguage,
    onUpload,
    onSwapFile,
    validationMessage,
    isStartingUpload,
}) => {
    return (
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: "16px"}}>
            <MediaPreviewCard file={selectedFile} showMeta={true}/>

            <Box
                sx={{
                    width: "100%",
                    borderRadius: "12px",
                    border: "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-surface)",
                    px: "14px",
                    py: "14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                }}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "18px",
                    }}
                >
                    Content language
                </Box>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                    }}
                >
                    Set language for speech transcription quality.
                </Box>
                <Box sx={{width: "234px"}}>
                    <NewTranscriptionLangDropdown
                        selectedLanguage={selectedLanguage}
                        onChange={setSelectedLanguage}
                        compact={true}
                    />
                </Box>
            </Box>

            {validationMessage ? (
                <Box
                    sx={{
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {validationMessage}
                </Box>
            ) : null}

            <Box
                sx={{
                    width: "100%",
                    height: "52px",
                    borderRadius: "999px",
                    backgroundColor: "#1a1a1a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isStartingUpload ? "default" : "pointer",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                        backgroundColor: isStartingUpload ? "#1a1a1a" : "#111827",
                    },
                    opacity: isStartingUpload ? 0.8 : 1,
                }}
                onClick={isStartingUpload ? undefined : onUpload}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#FFFFFF",
                        lineHeight: "20px",
                        letterSpacing: "0.01em",
                    }}
                >
                    {isStartingUpload ? "Preparing upload..." : "Upload"}
                </Box>
            </Box>

            <Box
                sx={{
                    width: "fit-content",
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--app-text-muted)",
                    cursor: "pointer",
                    "&:hover": {
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={onSwapFile}
            >
                Choose a different file
            </Box>
        </Box>
    );
};

const UploadAndEditWorkflow = ({
    onClose,
    projectId = null,
    initialFile = null,
    onUploadInitiated,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const {UploadAsset} = useAssetStore();

    const [selectedFile, setSelectedFile] = useState(initialFile);
    const [selectedLanguage, setSelectedLanguage] = useState("auto");
    const [validationMessage, setValidationMessage] = useState("");
    const [isStartingUpload, setIsStartingUpload] = useState(false);
    const [currentView, setCurrentView] = useState(
        initialFile ? viewStates.REVIEW_AND_UPLOAD : viewStates.SELECT_FILE
    );
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (initialFile) {
            setSelectedFile(initialFile);
            setCurrentView(viewStates.REVIEW_AND_UPLOAD);
            setValidationMessage("");
        }
    }, [initialFile]);

    const handleFileChoose = async (file) => {
        const isImageFile = Boolean(file?.type?.startsWith("image/")) || imageFilePattern.test(String(file?.name ?? ""));
        if (isImageFile) {
            setValidationMessage("Images are not supported. Upload an audio or video file.");
            return;
        }

        const validation = await validateFileForUpload(file);
        if (validation.mode !== "local_ok") {
            const extensionText = validation.codec
                ? `${validation.extension} (${validation.codec})`
                : validation.extension;
            setValidationMessage(`${extensionText} files are not supported in this workflow.`);
            return;
        }

        setSelectedFile(file);
        setCurrentView(viewStates.REVIEW_AND_UPLOAD);
        setValidationMessage("");
    };

    const handleUpload = async () => {
        if (!selectedFile || isStartingUpload) return;

        setIsStartingUpload(true);
        setValidationMessage("");

        try {
            const filetype = getUploadFiletype(selectedFile);
            const metadata = await ExtractMediaFileMetadata({
                file: selectedFile,
                fileType: filetype,
            });

            if (!metadata?.hasAudioStream) {
                if (isMountedRef.current) {
                    setValidationMessage("This file has no audio stream. Upload an audio or video file with speech.");
                }
                return;
            }

            let uploadProjectIdResolved = String(projectId || "").trim();
            let uploadAssetIdResolved = "";
            let didNotifyParent = false;

            const maybeNotifyParent = () => {
                if (didNotifyParent) return;
                const resolvedAssetId = String(uploadAssetIdResolved || "").trim();
                const resolvedProjectId = String(uploadProjectIdResolved || projectId || "").trim();
                if (!resolvedAssetId || !resolvedProjectId || !onUploadInitiated) return;

                didNotifyParent = true;
                onUploadInitiated({
                    assetId: resolvedAssetId,
                    projectId: resolvedProjectId,
                    languageCode: selectedLanguage,
                });
            };

            const assetId = await UploadAsset({
                projectId: uploadProjectIdResolved || projectId,
                projectType: LibrettoProjectTypes.ProjectTypeEditor,
                file: selectedFile,
                filetype,
                languageCode: selectedLanguage,
                onUploadInitiated: ({assetId: serverAssetId, projectId: serverProjectId}) => {
                    const resolvedAssetId = String(serverAssetId || "").trim();
                    if (resolvedAssetId) uploadAssetIdResolved = resolvedAssetId;
                    const resolvedProjectId = String(serverProjectId || "").trim();
                    if (resolvedProjectId) uploadProjectIdResolved = resolvedProjectId;
                    maybeNotifyParent();
                },
            });

            if (!assetId) {
                throw new Error("Upload failed to return an asset id");
            }

            uploadAssetIdResolved = String(assetId || "").trim();
            maybeNotifyParent();

            if (!onUploadInitiated) {
                onClose?.();
            }
        } catch (error) {
            console.error("[UploadAndEditWorkflow] Upload failed", error);
            if (isMountedRef.current) {
                setValidationMessage("Could not start upload. Please try again.");
            }
        } finally {
            if (isMountedRef.current) {
                setIsStartingUpload(false);
            }
        }
    };

    const handleSwapFile = () => {
        setCurrentView(viewStates.SELECT_FILE);
        setValidationMessage("");
    };

    return (
        <Box
            sx={{
                width: "715px",
                borderRadius: "12px",
                border: "1px solid var(--app-border-subtle)",
                backgroundColor: "var(--app-bg-surface)",
                px: "24px",
                py: "24px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
            }}
        >
            <Header onClose={onClose}/>

            {currentView === viewStates.SELECT_FILE ? (
                <SelectFileState
                    onFileChoose={handleFileChoose}
                    validationMessage={validationMessage}
                    isDarkMode={isDarkMode}
                />
            ) : null}

            {currentView === viewStates.REVIEW_AND_UPLOAD ? (
                <ReviewState
                    selectedFile={selectedFile}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    onUpload={handleUpload}
                    onSwapFile={handleSwapFile}
                    validationMessage={validationMessage}
                    isStartingUpload={isStartingUpload}
                />
            ) : null}
        </Box>
    );
};

export default UploadAndEditWorkflow;
